import styled, { css } from 'styled-components';
import { ButtonVariant, ButtonSize } from './types';

const variantStyles = {
  default: css`
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    box-shadow: ${({ theme }) => theme.shadows.sm};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primaryDark};
      box-shadow: ${({ theme }) => theme.shadows.md};
    }
    &:active:not(:disabled) {
      box-shadow: ${({ theme }) => theme.shadows.sm};
      transform: translateY(0.5px);
    }
  `,
  destructive: css`
    background-color: ${({ theme }) => theme.colors.danger};
    color: white;
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.dangerDark};
    }
  `,
  outline: css`
    background-color: transparent;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.gray100};
      border-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.primary};
    }
  `,
  secondary: css`
    background-color: ${({ theme }) => theme.colors.secondary};
    color: white;
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.secondaryDark};
    }
  `,
  ghost: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.accent};
      color: ${({ theme }) => theme.colors.textAccent};
    }
  `,
  link: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
    text-underline-offset: 0.25rem;
    &:hover:not(:disabled) {
      text-decoration: underline;
    }
  `,
  muted: css`
    background-color: transparent;
    border: 1px solid ${({ theme }) => theme.colors.gray300};
    color: ${({ theme }) => theme.colors.textPrimary};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.gray100};
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  `,
  soft: css`
    background-color: ${({ theme }) => theme.colors.primaryLight};
    color: ${({ theme }) => theme.colors.textPrimary};
    border: 1px solid transparent;
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.gray100};
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  `,
  tab: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.gray600};
    border: none;
    border-bottom: 2px solid transparent;
    border-radius: 0;
    &:hover:not(:disabled) {
      color: ${({ theme }) => theme.colors.primary};
    }
    &[data-active='true'] {
      color: ${({ theme }) => theme.colors.primary};
      border-bottom-color: ${({ theme }) => theme.colors.primary};
    }
  `,
  toggle: css`
    background-color: white;
    color: ${({ theme }) => theme.colors.gray800};
    border: 1px solid ${({ theme }) => theme.colors.gray300};
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.gray100};
    }
    &[data-active='true'] {
      background-color: ${({ theme }) => theme.colors.primaryLight};
      color: ${({ theme }) => theme.colors.dark};
      border-color: ${({ theme }) => theme.colors.primary};
    }
  `,
};

const sizeStyles = {
  default: css`
    height: 2.5rem;
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  `,
  sm: css`
    height: 2.25rem;
    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  `,
  lg: css`
    height: 2.75rem;
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[8]}`};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  `,
  icon: css`
    height: 2.5rem;
    width: 2.5rem;
    padding: 0;
  `,
};

export const StyledButton = styled.button<{ $variant: ButtonVariant; $size: ButtonSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  border: none;
  outline: none;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focusRing(theme.colors.primary)};
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  ${({ $variant }) => variantStyles[$variant]}
  ${({ $size }) => sizeStyles[$size]}
`;
